// LeverDsvTab.js — Tab: RoyETH v2 vs sroywstETH comparison.
// Shows share price chart, APY comparison chart, and per-day calculation breakdown.

import { useState, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine,
} from 'recharts';
import { useSession, useSessionDispatch } from './SessionContext';
import { DEFAULT_STRATEGIES, simulate } from '../../lib/dsv-engine';
import { getNavData, royethRateData } from '../../lib/nav-data-loader';
import { simulateRoyETHv2 } from '../../lib/leverdsv-engine';
import COLORS from '../../lib/colors';

// ── Formatting helpers ──

const fmtPct = (v, d = 2) => (v * 100).toFixed(d) + '%';
const fmtPctRaw = (v, d = 2) => v.toFixed(d) + '%';
const tickStyle = { fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" };
const chartMargin = { top: 10, right: 30, left: 10, bottom: 10 };
const mono = "'Fragment Mono', monospace";

// ── Rate data date range ──
const rateDataDates = Object.keys((royethRateData && royethRateData.data) || {}).sort();
const rateDataStart = rateDataDates.length > 0 ? rateDataDates[0] : null;
const rateDataEnd = rateDataDates.length > 0 ? rateDataDates[rateDataDates.length - 1] : null;

// ── Inline UI components ──

const Card = ({ title, children }) => (
  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4, padding: 16, marginBottom: 12 }}>
    {title && <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary, marginBottom: 12 }}>{title}</div>}
    {children}
  </div>
);

const MetricRow = ({ label, value, color, indent = 0 }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', paddingLeft: indent * 16 }}>
    <span style={{ fontFamily: mono, fontSize: 11, color: COLORS.textSecondary }}>{label}</span>
    <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 600, color: color || COLORS.ink }}>{value}</span>
  </div>
);

const Slider = ({ label, value, onChange, min, max, step, suffix = '' }) => {
  const decimals = step < 0.01 ? 3 : step < 0.1 ? 2 : step < 1 ? 1 : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>{label}</span>
        <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 600, color: COLORS.ink }}>
          {typeof value === 'number' ? value.toFixed(decimals) : value}{suffix}
        </span>
      </div>
      <input type="range" className="integrated-slider" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', height: 4, appearance: 'none', WebkitAppearance: 'none', background: `linear-gradient(to right, ${COLORS.accent} 0%, ${COLORS.accent} ${((value - min) / (max - min)) * 100}%, ${COLORS.borderDark} ${((value - min) / (max - min)) * 100}%, ${COLORS.borderDark} 100%)`, borderRadius: 2, outline: 'none', cursor: 'pointer' }} />
    </div>
  );
};

const Toggle = ({ label, value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
  >
    <div style={{
      width: 36, height: 20, borderRadius: 10,
      background: value ? COLORS.accent : COLORS.borderDark,
      border: `1px solid ${value ? COLORS.accent : COLORS.borderDark}`,
      position: 'relative', transition: 'all 0.2s', flexShrink: 0,
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 8,
        background: value ? COLORS.bg : COLORS.textTertiary,
        position: 'absolute', top: 1, left: value ? 18 : 1,
        transition: 'left 0.2s',
      }} />
    </div>
    {label && <span style={{ fontFamily: mono, fontSize: 11, color: COLORS.textSecondary }}>{label}</span>}
  </div>
);

const ChartTooltip = ({ active, payload, labelFormatter, valueFormatter }) => {
  if (!active || !payload?.length) return null;
  const fmt = valueFormatter || ((v) => typeof v === 'number' ? v.toFixed(4) : v);
  return (
    <div style={{ background: COLORS.ink, color: COLORS.bg, padding: '8px 12px', borderRadius: 2, fontFamily: mono, fontSize: 11, lineHeight: 1.6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
      {labelFormatter && <div style={{ marginBottom: 2 }}>{labelFormatter(payload[0]?.payload)}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
};

// ── Calculation detail row (for the breakdown panel) ──

const CalcRow = ({ label, value, op, color, bold, indent = 0 }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '3px 0', paddingLeft: indent * 16 }}>
    {op && <span style={{ fontFamily: mono, fontSize: 11, color: COLORS.textTertiary, width: 14, textAlign: 'right', flexShrink: 0 }}>{op}</span>}
    {!op && <span style={{ width: 14, flexShrink: 0 }} />}
    <span style={{ fontFamily: mono, fontSize: 11, color: COLORS.textSecondary, flex: 1 }}>{label}</span>
    <span style={{ fontFamily: mono, fontSize: 12, fontWeight: bold ? 700 : 400, color: color || COLORS.ink, whiteSpace: 'nowrap' }}>{value}</span>
  </div>
);

// ── Main component ──

export default function LeverDsvTab() {
  const { state } = useSession();
  const dispatch = useSessionDispatch();
  const [dateRangeMode, setDateRangeMode] = useState('custom90');
  const [customStartDate, setCustomStartDate] = useState('2025-11-28');
  const [selectedDayIdx, setSelectedDayIdx] = useState(null);

  const updateRoyethV2 = useCallback((patch) => {
    dispatch({ type: 'UPDATE_ROYETH_V2', patch });
  }, [dispatch]);

  // ── Compute DSV result ──
  const dsvResult = useMemo(() => {
    const strategies = DEFAULT_STRATEGIES.map(defaultStrat => {
      const alloc = state.dsv.allocations[defaultStrat.name];
      const base = alloc
        ? { ...defaultStrat, enabled: alloc.enabled, allocation: alloc.allocation }
        : { ...defaultStrat };

      if (!alloc?.overrideTab1 && defaultStrat.minCoverage > 0) {
        const market = state.markets[defaultStrat.name];
        if (market) {
          base.minCoverage = market.params.minCoverage;
          base.recoveryDuration = market.params.recoveryDuration;
          base.seniorFee = market.params.stFee;
          base.juniorFee = market.params.jtFee;
          base.yieldShareFee = market.params.ysFee;
          base.finalizedLossThreshold = market.liquidation.finalizedLossThreshold;
          base.selfLiquidationBonus = market.liquidation.selfLiquidationBonus;
        }
      }

      return base;
    });

    const navData = {};
    for (const strat of strategies) {
      if (!strat.enabled) continue;
      const nd = getNavData(strat.name);
      if (nd) {
        navData[strat.name] = { dates: nd.dates, navs: nd.navs };
      }
    }

    const simParams = state.dsv.simParams;
    return simulate({
      strategies,
      navData: Object.keys(navData).length > 0 ? navData : null,
      simParams: {
        reversionDays: simParams.reversionDays,
        liquidationBonusMode: simParams.liquidationBonusMode,
        liquidationRedemptionRate: simParams.liquidationRedemptionRate,
      },
    });
  }, [state.markets, state.dsv]);

  // ── Clamp to rate data availability ──
  const clampedDsvResult = useMemo(() => {
    if (!dsvResult?.dates?.length || !rateDataStart || !rateDataEnd) return dsvResult;
    const firstIdx = dsvResult.dates.findIndex(d => d >= rateDataStart);
    const lastIdx = dsvResult.dates.findLastIndex(d => d <= rateDataEnd);
    if (firstIdx < 0 || lastIdx < 0 || lastIdx <= firstIdx) return dsvResult;
    if (firstIdx === 0 && lastIdx === dsvResult.dates.length - 1) return dsvResult;
    const offset = firstIdx;
    const count = lastIdx - firstIdx + 1;
    if (count < 2) return dsvResult;
    const trimmedDates = dsvResult.dates.slice(offset, offset + count);
    const aggBase = dsvResult.aggregate.dsvCumReturn[offset];
    const trimmedStrategies = {};
    for (const [name, s] of Object.entries(dsvResult.strategies)) {
      const stBase = s.seniorCumReturn[offset];
      const uBase = s.underlyingCumReturn[offset];
      trimmedStrategies[name] = {
        ...s,
        seniorCumReturn: s.seniorCumReturn.slice(offset, offset + count).map(v => (1 + v) / (1 + stBase) - 1),
        underlyingCumReturn: s.underlyingCumReturn.slice(offset, offset + count).map(v => (1 + v) / (1 + uBase) - 1),
        jtEffectiveNav: s.jtEffectiveNav.slice(offset, offset + count),
        marketStateHistory: s.marketStateHistory?.slice(offset, offset + count),
      };
    }
    return {
      dates: trimmedDates,
      strategies: trimmedStrategies,
      aggregate: {
        ...dsvResult.aggregate,
        dsvCumReturn: dsvResult.aggregate.dsvCumReturn.slice(offset, offset + count).map(v => (1 + v) / (1 + aggBase) - 1),
      },
    };
  }, [dsvResult]);

  // ── Compute RoyETH v2 result ──
  const fullResult = useMemo(() => {
    if (!clampedDsvResult?.dates?.length || clampedDsvResult.dates.length < 2) return null;
    return simulateRoyETHv2({
      dsvResult: clampedDsvResult,
      rateData: royethRateData,
      config: {
        aaveLTV: state.royethV2.aaveLTV * 100,
        dsvLTV: state.royethV2.dsvLTV * 100,
        liqThreshold: state.royethV2.liqThreshold * 100,
        useHistoricRates: state.royethV2.useHistoricRates,
        stakingYieldOverride: state.royethV2.stakingYield * 100,
        aaveSupplyOverride: state.royethV2.aaveSupply * 100,
        usdcBorrowOverride: state.royethV2.usdcBorrow * 100,
        dsvBorrowOverride: state.royethV2.dsvBorrow * 100,
        dsvBorrowSpread: state.royethV2.dsvBorrowSpread * 100,
        ltvMode: state.royethV2.ltvMode,
        maxDsvRedemption: state.royethV2.maxDsvRedemption,
        harvestEnabled: state.royethV2.harvestEnabled,
        harvestIntervalDays: state.royethV2.harvestIntervalDays,
        sroywstETHLTV: state.royethV2.sroywstETHLTV * 100,
        sroywstETHBorrowPremium: state.royethV2.sroywstETHBorrowPremium * 100,
        sroywstETHAbsoluteRate: state.royethV2.sroywstETHAbsoluteRate,
        sroywstETHBorrowRate: state.royethV2.sroywstETHBorrowRate * 100,
        dsvBorrowAbsoluteRate: state.royethV2.dsvBorrowAbsoluteRate,
        dsvBorrowAbsolute: state.royethV2.dsvBorrowAbsolute * 100,
        royethV1Config: {
          aaveLTV: (state.royethV2.v1LTV != null ? state.royethV2.v1LTV : state.royeth.aaveLTV) * 100,
          useHistoricRates: state.royeth.useHistoricRates,
          stakingYieldOverride: state.royeth.stakingYield * 100,
          aaveSupplyOverride: state.royeth.aaveSupply * 100,
          usdcBorrowOverride: state.royeth.usdcBorrow * 100,
          ltvMode: state.royeth.ltvMode,
          maxDsvRedemption: state.royeth.maxDsvRedemption,
          harvestEnabled: state.royeth.harvestEnabled,
          harvestIntervalDays: state.royeth.harvestIntervalDays,
        },
      },
    });
  }, [clampedDsvResult, state.royethV2, state.royeth]);

  // ── Post-filter by date range ──
  const result = useMemo(() => {
    if (!fullResult) return null;
    const raw = fullResult;

    const trimResult = (r, offset, count) => {
      const days = Math.min(count, r.dates.length - offset);
      if (days < 2) return r;
      const slice = (arr) => arr.slice(offset, offset + days);
      const rebase = (arr) => {
        const base = arr[offset];
        return base > 0 ? slice(arr).map(v => v / base) : slice(arr);
      };
      const totalDays = (Date.parse(r.dates[offset + days - 1]) - Date.parse(r.dates[offset])) / 86400000;
      const lastVsEth = r.royethV2VsEth[offset + days - 1] / r.royethV2VsEth[offset];
      const totalReturn = lastVsEth - 1;
      const annualizedReturn = totalDays > 0 ? Math.pow(lastVsEth, 365 / totalDays) - 1 : 0;
      const sroywstETHLast = r.sroywstETHVsEth[offset + days - 1] / r.sroywstETHVsEth[offset];
      const sroywstETHReturn = sroywstETHLast - 1;
      const vsSroywstETHTotal = totalReturn - sroywstETHReturn;
      const lastVsSroywstETH = lastVsEth / sroywstETHLast;
      const vsSroywstETHAnnualized = totalDays > 0 ? Math.pow(lastVsSroywstETH, 365 / totalDays) - 1 : 0;
      const apySlice = r.dailyApy.slice(offset + 1, offset + days);
      const avgApy = apySlice.reduce((s, v) => s + v, 0) / apySlice.length;
      const dsvSlice = r.components.dsvYield.slice(offset + 1, offset + days);
      const avgDsvYield = dsvSlice.reduce((s, v) => s + v, 0) / dsvSlice.length;
      const brSlice = r.components.borrowRate.slice(offset + 1, offset + days);
      const avgBorrowRate = brSlice.reduce((s, v) => s + v, 0) / brSlice.length;

      return {
        dates: slice(r.dates),
        royethV2Nav: rebase(r.royethV2Nav),
        royethV2VsEth: rebase(r.royethV2VsEth),
        sroywstETHVsEth: rebase(r.sroywstETHVsEth),
        wstEthVsEth: rebase(r.wstEthVsEth),
        ethIndexed: rebase(r.ethIndexed),
        dailyApy: slice(r.dailyApy),
        sroywstETHDailyApy: slice(r.sroywstETHDailyApy),
        components: {
          staking: slice(r.components.staking),
          supply: slice(r.components.supply),
          carry: slice(r.components.carry),
          leveragedDsvIncome: slice(r.components.leveragedDsvIncome),
          wstETHDebtCost: slice(r.components.wstETHDebtCost),
          dsvDebtCost: slice(r.components.dsvDebtCost),
          dsvYield: slice(r.components.dsvYield),
          borrowRate: slice(r.components.borrowRate),
          dsvBorrowRate: slice(r.components.dsvBorrowRate),
        },
        sroywstETHComponents: {
          royethV1Apy: slice(r.sroywstETHComponents.royethV1Apy),
          borrowCost: slice(r.sroywstETHComponents.borrowCost),
          v1Staking: slice(r.sroywstETHComponents.v1Staking),
          v1Supply: slice(r.sroywstETHComponents.v1Supply),
          v1Carry: slice(r.sroywstETHComponents.v1Carry),
          v1DsvYield: slice(r.sroywstETHComponents.v1DsvYield),
          v1BorrowRate: slice(r.sroywstETHComponents.v1BorrowRate),
        },
        rawRates: slice(r.rawRates),
        effectiveLeverage: slice(r.effectiveLeverage),
        sroywstETHLeverage: r.sroywstETHLeverage,
        harvests: r.harvests?.filter(h =>
          h.date >= r.dates[offset] && h.date <= r.dates[offset + days - 1]
        ) || [],
        summary: {
          totalReturn, annualizedReturn, days: totalDays,
          avgApy, avgDsvYield, avgBorrowRate,
          sroywstETHReturn, vsSroywstETH: vsSroywstETHTotal, vsSroywstETHAnnualized,
          dsvMultiplier: r.summary.dsvMultiplier,
        },
      };
    };

    if (dateRangeMode === 'latest90' && raw.dates.length > 91) {
      return trimResult(raw, raw.dates.length - 91, 91);
    }
    if (dateRangeMode === 'custom90' && customStartDate && raw.dates.length > 0) {
      const startIdx = raw.dates.findIndex(d => d >= customStartDate);
      if (startIdx >= 0) {
        return trimResult(raw, startIdx, 91);
      }
    }
    return raw;
  }, [fullResult, dateRangeMode, customStartDate]);

  // ── Chart data: share price ──
  const vsEthData = useMemo(() => {
    if (!result) return [];
    return result.dates.map((date, i) => ({
      date,
      royethV2VsEth: result.royethV2VsEth[i],
      sroywstETHVsEth: result.sroywstETHVsEth[i],
      ethIndexed: 1.0,
    }));
  }, [result]);

  // ── Chart data: APY comparison ──
  const apyCompareData = useMemo(() => {
    if (!result || result.dates.length < 2) return [];
    return result.dates.map((date, i) => {
      if (i === 0) return null;
      return {
        date,
        royethV2Apy: result.dailyApy[i],
        sroywstETHApy: result.sroywstETHDailyApy[i],
      };
    }).filter(Boolean);
  }, [result]);

  // ── Selected day data for calculation breakdown ──
  const selectedDay = useMemo(() => {
    if (!result || selectedDayIdx == null || selectedDayIdx < 1 || selectedDayIdx >= result.dates.length) return null;
    const i = selectedDayIdx;
    const rates = result.rawRates[i];
    if (!rates) return null;
    const dsvMult = result.summary.dsvMultiplier;
    const v2LTV = state.royethV2.aaveLTV;
    const effLev = result.effectiveLeverage[i];
    const sLev = result.sroywstETHLeverage;
    return {
      date: result.dates[i],
      rates,
      v2: {
        ltv: v2LTV,
        dsvMultiplier: dsvMult,
        effectiveLeverage: effLev,
        staking: result.components.staking[i],
        supply: result.components.supply[i],
        dsvYield: result.components.dsvYield[i],
        usdcBorrowRate: result.components.borrowRate[i],
        dsvBorrowRate: result.components.dsvBorrowRate[i],
        leveragedDsvIncome: result.components.leveragedDsvIncome[i],
        wstETHDebtCost: result.components.wstETHDebtCost[i],
        dsvDebtCost: result.components.dsvDebtCost[i],
        carry: result.components.carry[i],
        totalApy: result.dailyApy[i],
      },
      sroywstETH: {
        leverage: sLev,
        royethV1Apy: result.sroywstETHComponents.royethV1Apy[i],
        borrowCost: result.sroywstETHComponents.borrowCost[i],
        totalApy: result.sroywstETHDailyApy[i],
        v1Staking: result.sroywstETHComponents.v1Staking[i],
        v1Supply: result.sroywstETHComponents.v1Supply[i],
        v1Carry: result.sroywstETHComponents.v1Carry[i],
        v1DsvYield: result.sroywstETHComponents.v1DsvYield[i],
        v1BorrowRate: result.sroywstETHComponents.v1BorrowRate[i],
      },
    };
  }, [result, selectedDayIdx, state.royethV2.aaveLTV]);

  const summary = result?.summary;

  // ── Handle chart click to select a day ──
  const handleChartClick = useCallback((data) => {
    if (!data?.activeLabel || !result) return;
    const idx = result.dates.indexOf(data.activeLabel);
    if (idx > 0) setSelectedDayIdx(idx);
  }, [result]);

  // ── Render ──
  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, alignItems: 'start' }}>
        {/* ── Left Panel: Config ── */}
        <div>
          <Card title="Aave Config — RoyETH v2">
            <Slider
              label="wstETH LTV"
              value={Math.round(state.royethV2.aaveLTV * 100)}
              onChange={(v) => updateRoyethV2({ aaveLTV: v / 100 })}
              min={10} max={75} step={1} suffix="%"
            />
            <Slider
              label="Liquidation Threshold"
              value={Math.round(state.royethV2.liqThreshold * 100)}
              onChange={(v) => updateRoyethV2({ liqThreshold: v / 100 })}
              min={70} max={99} step={1} suffix="%"
            />
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>LTV Mode</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {['fixed', 'dynamic'].map((mode) => (
                    <button key={mode} onClick={() => updateRoyethV2({ ltvMode: mode })} style={{
                      fontFamily: mono, fontSize: 11, padding: '3px 10px',
                      background: state.royethV2.ltvMode === mode ? COLORS.ink : 'transparent',
                      color: state.royethV2.ltvMode === mode ? COLORS.bg : COLORS.textSecondary,
                      border: `1px solid ${state.royethV2.ltvMode === mode ? COLORS.ink : COLORS.borderLight}`,
                      borderRadius: 3, cursor: 'pointer', textTransform: 'capitalize',
                    }}>
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {state.royethV2.ltvMode === 'dynamic' && (
              <Slider
                label="Max DSV Redemption / Day"
                value={state.royethV2.maxDsvRedemption}
                onChange={(v) => updateRoyethV2({ maxDsvRedemption: v })}
                min={0} max={20} step={0.5} suffix="%"
              />
            )}
          </Card>

          <Card title="DSV Lever Loop — RoyETH v2">
            <Slider
              label="DSV LTV (loop leverage)"
              value={Math.round(state.royethV2.dsvLTV * 100)}
              onChange={(v) => updateRoyethV2({ dsvLTV: v / 100 })}
              min={0} max={90} step={1} suffix="%"
            />
            <MetricRow
              label="Effective DSV Multiplier"
              value={`${(1 / (1 - state.royethV2.dsvLTV)).toFixed(2)}x`}
              color={COLORS.accent}
            />
            <MetricRow
              label="Total Leverage (wstETH LTV x DSV mult)"
              value={`${(state.royethV2.aaveLTV / (1 - state.royethV2.dsvLTV)).toFixed(2)}x`}
              color={COLORS.accent}
            />
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <Toggle
                label={state.royethV2.dsvBorrowAbsoluteRate ? 'Absolute DSV Borrow Rate' : 'DSV Borrow = USDC Rate + Spread'}
                value={state.royethV2.dsvBorrowAbsoluteRate}
                onChange={(v) => updateRoyethV2({ dsvBorrowAbsoluteRate: v })}
              />
            </div>
            {state.royethV2.dsvBorrowAbsoluteRate ? (
              <Slider
                label="DSV Borrow Rate (absolute)"
                value={Math.round(state.royethV2.dsvBorrowAbsolute * 1000) / 10}
                onChange={(v) => updateRoyethV2({ dsvBorrowAbsolute: v / 100 })}
                min={0} max={20} step={0.1} suffix="%"
              />
            ) : (
              <>
                <Slider
                  label="DSV Borrow Spread"
                  value={Math.round(state.royethV2.dsvBorrowSpread * 1000) / 10}
                  onChange={(v) => updateRoyethV2({ dsvBorrowSpread: v / 100 })}
                  min={0} max={5} step={0.1} suffix="%"
                />
                <div style={{ fontFamily: mono, fontSize: 10, color: COLORS.textTertiary, lineHeight: 1.5 }}>
                  Added on top of USDC borrow rate for DSV-collateralized borrows
                </div>
              </>
            )}
          </Card>

          <Card title="Lever Loop — sroywstETH">
            <Slider
              label="sroywstETH LTV"
              value={Math.round(state.royethV2.sroywstETHLTV * 100)}
              onChange={(v) => updateRoyethV2({ sroywstETHLTV: v / 100 })}
              min={0} max={90} step={1} suffix="%"
            />
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <Toggle
                label={state.royethV2.sroywstETHAbsoluteRate ? 'Absolute wstETH Borrow Rate' : 'wstETH Borrow = Staking + Premium'}
                value={state.royethV2.sroywstETHAbsoluteRate}
                onChange={(v) => updateRoyethV2({ sroywstETHAbsoluteRate: v })}
              />
            </div>
            {state.royethV2.sroywstETHAbsoluteRate ? (
              <Slider
                label="wstETH Borrow Rate (absolute)"
                value={Math.round(state.royethV2.sroywstETHBorrowRate * 1000) / 10}
                onChange={(v) => updateRoyethV2({ sroywstETHBorrowRate: v / 100 })}
                min={0} max={10} step={0.1} suffix="%"
              />
            ) : (
              <Slider
                label="wstETH Borrow Premium"
                value={Math.round(state.royethV2.sroywstETHBorrowPremium * 1000) / 10}
                onChange={(v) => updateRoyethV2({ sroywstETHBorrowPremium: v / 100 })}
                min={0} max={5} step={0.1} suffix="%"
              />
            )}
            <MetricRow
              label="sroywstETH Leverage"
              value={`${(1 / (1 - state.royethV2.sroywstETHLTV)).toFixed(2)}x`}
              color={COLORS.ethBlue}
            />
            <div style={{ marginTop: 12, borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: 12 }}>
              <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>Underlying RoyETH v1</div>
              <Slider
                label="v1 Carry LTV"
                value={Math.round((state.royethV2.v1LTV != null ? state.royethV2.v1LTV : state.royeth.aaveLTV) * 100)}
                onChange={(v) => updateRoyethV2({ v1LTV: v / 100 })}
                min={10} max={75} step={1} suffix="%"
              />
              {state.royethV2.v1LTV != null && state.royethV2.v1LTV !== state.royeth.aaveLTV && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontFamily: mono, fontSize: 10, color: COLORS.textTertiary }}>
                    Tab 3 default: {Math.round(state.royeth.aaveLTV * 100)}%
                  </span>
                  <button
                    onClick={() => updateRoyethV2({ v1LTV: null })}
                    style={{ fontFamily: mono, fontSize: 10, color: COLORS.accent, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Reset to Tab 3
                  </button>
                </div>
              )}
              <MetricRow label="v1 LTV Mode" value={state.royeth.ltvMode} />
            </div>
          </Card>

          <Card title="Rate Source — RoyETH v2">
            <Toggle
              label={state.royethV2.useHistoricRates ? 'Historic Rates' : 'Override Rates'}
              value={state.royethV2.useHistoricRates}
              onChange={(v) => updateRoyethV2({ useHistoricRates: v })}
            />
            {!state.royethV2.useHistoricRates && (
              <div style={{ marginTop: 12 }}>
                <Slider label="Staking Yield" value={Math.round(state.royethV2.stakingYield * 1000) / 10} onChange={(v) => updateRoyethV2({ stakingYield: v / 100 })} min={0} max={8} step={0.1} suffix="%" />
                <Slider label="Aave Supply" value={Math.round(state.royethV2.aaveSupply * 10000) / 100} onChange={(v) => updateRoyethV2({ aaveSupply: v / 100 })} min={0} max={1} step={0.01} suffix="%" />
                <Slider label="USDC Borrow (vs wstETH collateral)" value={Math.round(state.royethV2.usdcBorrow * 1000) / 10} onChange={(v) => updateRoyethV2({ usdcBorrow: v / 100 })} min={0} max={20} step={0.1} suffix="%" />
                <Slider label="USDC Borrow (vs DSV collateral)" value={Math.round(state.royethV2.dsvBorrow * 1000) / 10} onChange={(v) => updateRoyethV2({ dsvBorrow: v / 100 })} min={0} max={20} step={0.1} suffix="%" />
              </div>
            )}
          </Card>

          <Card title="Harvest & Compound — RoyETH v2">
            <Toggle
              label="Enable Harvest"
              value={state.royethV2.harvestEnabled}
              onChange={(v) => updateRoyethV2({ harvestEnabled: v })}
            />
            {state.royethV2.harvestEnabled && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>Harvest Interval</span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <input type="number" min={1} value={state.royethV2.harvestIntervalDays}
                      onChange={(e) => { const v = parseInt(e.target.value, 10); if (v >= 1) updateRoyethV2({ harvestIntervalDays: v }); }}
                      style={{ width: 48, textAlign: 'right', fontFamily: mono, fontSize: 13, fontWeight: 600, color: COLORS.ink, background: COLORS.bg, border: `1px solid ${COLORS.borderLight}`, borderRadius: 3, padding: '2px 6px', outline: 'none' }}
                    />
                    <span style={{ fontFamily: mono, fontSize: 11, color: COLORS.textSecondary }}>days</span>
                  </div>
                </div>
              </div>
            )}
          </Card>

          <Card title="Data Sources">
            {clampedDsvResult?.dates?.length >= 2 ? (
              <>
                <MetricRow label="DSV data" value={`${clampedDsvResult.dates[0]} → ${clampedDsvResult.dates[clampedDsvResult.dates.length - 1]}`} />
                <MetricRow label="Rate data" value={`${rateDataStart} → ${rateDataEnd}`} />
                <MetricRow label="Compatible range" value={`${clampedDsvResult.dates.length} days`} />
              </>
            ) : (
              <div style={{ fontFamily: mono, fontSize: 12, color: COLORS.textTertiary, padding: '8px 0' }}>
                No overlapping data between DSV and rate sources.
              </div>
            )}
          </Card>

          {summary && (
            <Card title="Summary — RoyETH v2">
              <MetricRow label="DSV Multiplier" value={`${summary.dsvMultiplier.toFixed(2)}x`} color={COLORS.accent} />
              <MetricRow label="Total Return (vs ETH)" value={fmtPct(summary.totalReturn)} color={summary.totalReturn >= 0 ? COLORS.positive : COLORS.negative} />
              <MetricRow label="Annualized Return (vs ETH)" value={fmtPct(summary.annualizedReturn)} color={summary.annualizedReturn >= 0 ? COLORS.positive : COLORS.negative} />
              <MetricRow label="Average APY" value={fmtPctRaw(summary.avgApy)} color={COLORS.accent} />
            </Card>
          )}

          {summary && (
            <Card title="Summary — sroywstETH">
              <MetricRow label="Leverage" value={`${(1 / (1 - state.royethV2.sroywstETHLTV)).toFixed(2)}x`} color={COLORS.ethBlue} />
              <MetricRow label="Total Return (vs ETH)" value={fmtPct(summary.sroywstETHReturn)} color={summary.sroywstETHReturn >= 0 ? COLORS.positive : COLORS.negative} />
            </Card>
          )}

          {summary && (
            <Card title="Comparison">
              <MetricRow label="v2 vs sroywstETH (total)" value={fmtPct(summary.vsSroywstETH)} color={summary.vsSroywstETH >= 0 ? COLORS.positive : COLORS.negative} />
              <MetricRow label="v2 vs sroywstETH (annualized)" value={fmtPct(summary.vsSroywstETHAnnualized)} color={summary.vsSroywstETHAnnualized >= 0 ? COLORS.positive : COLORS.negative} />
              <MetricRow label="Period" value={`${Math.round(summary.days)} days`} />
            </Card>
          )}
        </div>

        {/* ── Right Panel: Charts + Calculation Breakdown ── */}
        <div>
          {/* Date range selector */}
          <Card title="Date Range">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px', background: COLORS.surfaceAlt, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4 }}>
                {[
                  { key: 'full', label: 'Max compatible' },
                  { key: 'latest90', label: 'Latest 90d' },
                  { key: 'custom90', label: '90d from' },
                ].map(({ key, label }) => (
                  <button key={key} onClick={() => setDateRangeMode(key)} style={{
                    fontFamily: mono, fontSize: 11, padding: '4px 10px',
                    background: dateRangeMode === key ? COLORS.ink : 'transparent',
                    color: dateRangeMode === key ? COLORS.bg : COLORS.textSecondary,
                    border: 'none', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </button>
                ))}
                {dateRangeMode === 'custom90' && (
                  <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)}
                    style={{ fontFamily: mono, fontSize: 11, padding: '3px 6px', background: COLORS.bg, color: COLORS.ink, border: `1px solid ${COLORS.borderLight}`, borderRadius: 3, outline: 'none', marginLeft: 2 }}
                  />
                )}
              </div>
              {result?.dates?.length > 0 && (
                <span style={{ fontFamily: mono, fontSize: 11, color: COLORS.textTertiary }}>
                  {result.dates[0]} → {result.dates[result.dates.length - 1]} ({Math.round(result.summary?.days || 0)}d)
                </span>
              )}
            </div>
          </Card>

          {!result ? (
            <Card>
              <div style={{ padding: 24, textAlign: 'center', color: COLORS.textTertiary, fontFamily: mono, fontSize: 12 }}>
                {clampedDsvResult?.dates?.length >= 2
                  ? 'Computing backtest...'
                  : 'No overlapping data between DSV portfolio and rate sources.'}
              </div>
            </Card>
          ) : (
            <>
              {/* 1. Share Price Chart */}
              <Card title="Share Price (indexed to ETH)">
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={vsEthData} margin={chartMargin} onClick={handleChartClick}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                    <XAxis dataKey="date" tick={tickStyle} tickFormatter={(d) => d.slice(5)} interval={Math.max(1, Math.floor(vsEthData.length / 12))} />
                    <YAxis tickFormatter={(v) => v.toFixed(4)} tick={tickStyle} width={60} domain={['auto', 'auto']} />
                    <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} valueFormatter={(v) => v.toFixed(6)} />} />
                    {selectedDay && <ReferenceLine x={selectedDay.date} stroke={COLORS.ink} strokeDasharray="2 2" />}
                    <Line type="monotone" dataKey="ethIndexed" stroke={COLORS.textTertiary} strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="ETH = 1.0" />
                    <Line type="monotone" dataKey="sroywstETHVsEth" stroke={COLORS.ethBlue} strokeWidth={1.5} dot={false} name="sroywstETH" />
                    <Line type="monotone" dataKey="royethV2VsEth" stroke={'#E8A838'} strokeWidth={2} dot={false} name="RoyETH v2" />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              {/* 2. APY Comparison Chart */}
              <Card title="APY Comparison (annualized %)">
                {apyCompareData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={apyCompareData} margin={chartMargin} onClick={handleChartClick}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                      <XAxis dataKey="date" tick={tickStyle} tickFormatter={(d) => d.slice(5)} interval={Math.max(1, Math.floor(apyCompareData.length / 12))} />
                      <YAxis tickFormatter={(v) => v.toFixed(1) + '%'} tick={tickStyle} width={60} />
                      <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} valueFormatter={(v) => v != null ? v.toFixed(2) + '%' : '—'} />} />
                      {selectedDay && <ReferenceLine x={selectedDay.date} stroke={COLORS.ink} strokeDasharray="2 2" />}
                      <Line type="monotone" dataKey="sroywstETHApy" stroke={COLORS.ethBlue} strokeWidth={1.5} dot={false} name="sroywstETH APY" />
                      <Line type="monotone" dataKey="royethV2Apy" stroke={'#E8A838'} strokeWidth={2} dot={false} name="RoyETH v2 APY" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: COLORS.textTertiary, fontFamily: mono, fontSize: 12 }}>
                    Not enough data for APY comparison.
                  </div>
                )}
                <div style={{ fontFamily: mono, fontSize: 10, color: COLORS.textTertiary, marginTop: 8 }}>
                  Click on either chart to select a day and see the full calculation breakdown below.
                </div>
              </Card>

              {/* 3. Calculation Breakdown for Selected Day */}
              {selectedDay ? (
                <Card title={`Calculation Breakdown — ${selectedDay.date}`}>
                  {/* Market rates on this day */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>Market Rates</div>
                    <MetricRow label="wstETH Staking Yield" value={fmtPctRaw(selectedDay.rates.stakingYield)} />
                    <MetricRow label="Aave wstETH Supply APY" value={fmtPctRaw(selectedDay.rates.aaveSupply)} />
                    <MetricRow label="USDC Borrow Rate (vs wstETH collateral)" value={fmtPctRaw(selectedDay.rates.usdcBorrow)} />
                    <MetricRow label="USDC Borrow Rate (vs DSV collateral)" value={fmtPctRaw(selectedDay.rates.dsvBorrow)} />
                    <MetricRow label="DSV Senior Yield" value={fmtPctRaw(selectedDay.rates.dsvYield)} />
                    <MetricRow label="wstETH Borrow Rate" value={fmtPctRaw(selectedDay.rates.wstEthBorrowRate)} />
                    {!state.royethV2.sroywstETHAbsoluteRate && (
                      <MetricRow label="  (= staking + premium)" value={`${fmtPctRaw(selectedDay.rates.stakingYield)} + ${fmtPctRaw(selectedDay.rates.wstEthBorrowPremium)}`} />
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                    {/* RoyETH v2 breakdown */}
                    <div style={{ borderRight: `1px solid ${COLORS.borderLight}`, paddingRight: 16 }}>
                      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#E8A838', marginBottom: 10, fontWeight: 700 }}>RoyETH v2</div>

                      <CalcRow label="wstETH Staking Yield" value={fmtPctRaw(selectedDay.v2.staking)} />
                      <CalcRow op="+" label="Aave Supply APY" value={fmtPctRaw(selectedDay.v2.supply)} />
                      <CalcRow op="+" label="Leveraged Carry:" value="" bold />

                      {/* DSV income */}
                      <CalcRow indent={1} label="DSV Income (leveraged):" value={fmtPctRaw(selectedDay.v2.leveragedDsvIncome)} color={COLORS.positive} bold />
                      <CalcRow indent={2} label="DSV Yield" value={fmtPctRaw(selectedDay.v2.dsvYield)} />
                      <CalcRow indent={2} op="×" label="wstETH LTV" value={`${(selectedDay.v2.ltv * 100).toFixed(0)}%`} />
                      <CalcRow indent={2} op="×" label="DSV Multiplier" value={`${selectedDay.v2.dsvMultiplier.toFixed(2)}x`} />

                      {/* wstETH-collateral USDC debt */}
                      <CalcRow indent={1} op="−" label="USDC debt cost (vs wstETH collateral):" value={fmtPctRaw(selectedDay.v2.wstETHDebtCost)} color={COLORS.negative} bold />
                      <CalcRow indent={2} label="USDC Borrow Rate" value={fmtPctRaw(selectedDay.v2.usdcBorrowRate)} />
                      <CalcRow indent={2} op="×" label="wstETH LTV" value={`${(selectedDay.v2.ltv * 100).toFixed(0)}%`} />

                      {/* DSV-collateral USDC debt */}
                      <CalcRow indent={1} op="−" label="USDC debt cost (vs DSV collateral):" value={fmtPctRaw(selectedDay.v2.dsvDebtCost)} color={COLORS.negative} bold />
                      <CalcRow indent={2} label="USDC Borrow Rate (w/ spread)" value={fmtPctRaw(selectedDay.v2.dsvBorrowRate)} />
                      <CalcRow indent={2} op="×" label="wstETH LTV" value={`${(selectedDay.v2.ltv * 100).toFixed(0)}%`} />
                      <CalcRow indent={2} op="×" label="DSV Multiplier − 1" value={`${(selectedDay.v2.dsvMultiplier - 1).toFixed(2)}x`} />

                      <CalcRow op="=" label="Net Carry" value={fmtPctRaw(selectedDay.v2.carry)} color={selectedDay.v2.carry >= 0 ? COLORS.positive : COLORS.negative} bold />

                      <div style={{ borderTop: `1px solid ${COLORS.borderLight}`, marginTop: 8, paddingTop: 8 }}>
                        <CalcRow op="=" label="Total APY" value={fmtPctRaw(selectedDay.v2.totalApy)} color={'#E8A838'} bold />
                      </div>
                    </div>

                    {/* sroywstETH breakdown */}
                    <div style={{ paddingLeft: 4 }}>
                      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.ethBlue, marginBottom: 10, fontWeight: 700 }}>sroywstETH</div>

                      <CalcRow label={`RoyETH v1 APY × Leverage`} value="" />
                      <CalcRow label={`${fmtPctRaw(selectedDay.sroywstETH.royethV1Apy)} × ${selectedDay.sroywstETH.leverage.toFixed(2)}x`} value={fmtPctRaw(selectedDay.sroywstETH.leverage * selectedDay.sroywstETH.royethV1Apy)} color={COLORS.textTertiary} />

                      <CalcRow op="−" label="wstETH Borrow Cost × (Leverage − 1)" value="" />
                      <CalcRow label={`${fmtPctRaw(selectedDay.rates.wstEthBorrowRate)} × ${(selectedDay.sroywstETH.leverage - 1).toFixed(2)}`} value={fmtPctRaw(selectedDay.sroywstETH.borrowCost)} color={COLORS.negative} />

                      <div style={{ borderTop: `1px solid ${COLORS.borderLight}`, marginTop: 8, paddingTop: 8 }}>
                        <CalcRow op="=" label="Total APY" value={fmtPctRaw(selectedDay.sroywstETH.totalApy)} color={COLORS.ethBlue} bold />
                      </div>

                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 6 }}>RoyETH v1 breakdown (LTV {Math.round((state.royethV2.v1LTV != null ? state.royethV2.v1LTV : state.royeth.aaveLTV) * 100)}%)</div>
                        <CalcRow label="Staking Yield" value={fmtPctRaw(selectedDay.sroywstETH.v1Staking)} />
                        <CalcRow op="+" label="Aave Supply" value={fmtPctRaw(selectedDay.sroywstETH.v1Supply)} />
                        <CalcRow op="+" label="v1 Carry:" value={fmtPctRaw(selectedDay.sroywstETH.v1Carry)} color={selectedDay.sroywstETH.v1Carry >= 0 ? COLORS.positive : COLORS.negative} bold />
                        <CalcRow indent={1} label="DSV Yield" value={fmtPctRaw(selectedDay.sroywstETH.v1DsvYield)} />
                        <CalcRow indent={1} op="−" label="USDC Borrow Rate" value={fmtPctRaw(selectedDay.sroywstETH.v1BorrowRate)} color={COLORS.negative} />
                        <CalcRow indent={1} label={`× LTV ${Math.round((state.royethV2.v1LTV != null ? state.royethV2.v1LTV : state.royeth.aaveLTV) * 100)}%`} value="" color={COLORS.textTertiary} />
                        <div style={{ borderTop: `1px solid ${COLORS.borderLight}`, marginTop: 4, paddingTop: 4 }}>
                          <CalcRow op="=" label="v1 Total APY" value={fmtPctRaw(selectedDay.sroywstETH.royethV1Apy)} bold />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comparison */}
                  <div style={{ borderTop: `2px solid ${COLORS.borderLight}`, marginTop: 16, paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 700, color: COLORS.textSecondary }}>Difference (RoyETH v2 − sroywstETH)</span>
                    <span style={{ fontFamily: mono, fontSize: 14, fontWeight: 700, color: (selectedDay.v2.totalApy - selectedDay.sroywstETH.totalApy) >= 0 ? COLORS.positive : COLORS.negative }}>
                      {(selectedDay.v2.totalApy - selectedDay.sroywstETH.totalApy) >= 0 ? '+' : ''}{fmtPctRaw(selectedDay.v2.totalApy - selectedDay.sroywstETH.totalApy)}
                    </span>
                  </div>
                </Card>
              ) : (
                result && (
                  <Card>
                    <div style={{ padding: 24, textAlign: 'center', color: COLORS.textTertiary, fontFamily: mono, fontSize: 12 }}>
                      Click on a data point in either chart to see a full calculation breakdown for that day.
                    </div>
                  </Card>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
